import ApolloClient from 'apollo-client';

import { IOffersFragment } from 'generated/graphql-gateway';
import {
  ILoyaltyOfferConfigsQuery,
  ILoyaltyOfferTemplateFragment,
  ILoyaltyOfferTemplatesQuery,
  IPlaceholderFieldsQuery,
  LoyaltyOfferConfigsDocument,
  LoyaltyOfferTemplatesDocument,
  Maybe,
  PlaceholderFieldsDocument,
} from 'generated/sanity-graphql';

import { LoyaltyOffer } from '../../types';

const buildEngineIdMap = <T>(
  data?: readonly (T & { loyaltyEngineId: Maybe<string> })[]
): Record<string, T> => {
  return (data || []).reduce<Record<string, T & { loyaltyEngineId: Maybe<string> }>>((acc, val) => {
    const { loyaltyEngineId } = val;
    if (loyaltyEngineId) {
      acc[loyaltyEngineId] = val;
    }
    return acc;
  }, {});
};

export const fetchPersonalizedOfferData = async ({
  client,
  uri,
}: {
  client: ApolloClient<object>;
  uri: string;
}) => {
  const [configs, templates, placeholderFields] = await Promise.all([
    client.query<ILoyaltyOfferConfigsQuery>({
      query: LoyaltyOfferConfigsDocument,
      context: { uri },
    }),
    client.query<ILoyaltyOfferTemplatesQuery>({
      query: LoyaltyOfferTemplatesDocument,
      context: { uri },
    }),
    client.query<IPlaceholderFieldsQuery>({
      query: PlaceholderFieldsDocument,
      context: { uri },
    }),
  ]);

  const configMap = buildEngineIdMap(configs?.data?.allSystemwideOffers);
  const templateMap = buildEngineIdMap(templates?.data?.allOfferTemplates);

  return { configMap, templateMap, placeholderFields };
};

type Await<T> = T extends Promise<infer U> ? U : T;

interface IMergePersonalizedData {
  configMap: Await<ReturnType<typeof fetchPersonalizedOfferData>>['configMap'];
  templateMap: Await<ReturnType<typeof fetchPersonalizedOfferData>>['templateMap'];
  placeholderFields: Await<ReturnType<typeof fetchPersonalizedOfferData>>['placeholderFields'];
  validOffer: IOffersFragment;
}

interface IPersonalizedOffer extends Omit<LoyaltyOffer, 'name' | 'description'> {
  name: ILoyaltyOfferTemplateFragment['name'];
  description: ILoyaltyOfferTemplateFragment['description'];
}

export const mergePersonalizedData = ({
  validOffer,
  configMap,
  templateMap,
}: IMergePersonalizedData): LoyaltyOffer => {
  const { templateId, configId } = validOffer || {};
  const config = configMap[configId || ''];
  const template = templateMap[templateId || ''];

  const { name, description, backgroundImage } = template || {};

  // TO DO: interpolate values in name / description

  // Override offer config data with name / description / backgroundImage from template
  const mergedOffer: IPersonalizedOffer = {
    ...config,
    // Template overrides for personalization
    name,
    description,
    isStackable: validOffer.isStackable,
    ...(backgroundImage && { backgroundImage }),
  };

  // Temporarily asserting this to Loyalty Offer.
  // LocaleSmartBlockContent will need to be parsed / interpolated to match LoyaltyOffer name and description
  return mergedOffer as LoyaltyOffer;
};
